import { Injectable } from '@angular/core';
import { BehaviorSubject, take } from 'rxjs';
import { Option } from 'rcp-common-lib';
import {
    DEFAULT_CLEARED_NOTIFICATION_PAGING,
    DEFAULT_OPEN_NOTIFICATION_PAGING,
    DEFAULT_WORKLIST_PAGESIZE,
    NOTIFICATIONS_CLEARED_FILTER_OPTIONS,
    NOTIFICATIONS_OPEN_FILTER_OPTIONS,
} from '@core/constants/worklist-search.constants';
import {
    InstantRange,
    LocalDateTimeRange,
    NotificationControllerService,
    NotificationResponse,
    PagedNotificationSearchRequest,
    PagedResponseNotificationResponse,
    SortablePagedRequestNotificationSortBy,
} from '@core/generated/openapi';
import { PageEvent } from '@angular/material/paginator';
import moment from 'moment';
import { EnumService } from '@services/enum/enum.service';
import StatusListEnum = PagedNotificationSearchRequest.StatusListEnum;
import SortFieldNameEnum = SortablePagedRequestNotificationSortBy.SortFieldNameEnum;
import SortDirectionEnum = SortablePagedRequestNotificationSortBy.SortDirectionEnum;

@Injectable({
    providedIn: 'root',
})
export class WorklistUserNotificationService {
    notificationOpenFilter: BehaviorSubject<Option> =
        new BehaviorSubject<Option>(NOTIFICATIONS_OPEN_FILTER_OPTIONS[3]);

    notificationClearedFilter: BehaviorSubject<Option> =
        new BehaviorSubject<Option>(NOTIFICATIONS_CLEARED_FILTER_OPTIONS[3]);

    PAGED_OPEN_NOTIFICATIONS: BehaviorSubject<PagedResponseNotificationResponse> =
        new BehaviorSubject<PagedResponseNotificationResponse>(null);

    PAGED_CLEARED_NOTIFICATIONS: BehaviorSubject<PagedResponseNotificationResponse> =
        new BehaviorSubject<PagedResponseNotificationResponse>(null);

    openPaging: SortablePagedRequestNotificationSortBy = JSON.parse(
        JSON.stringify(DEFAULT_OPEN_NOTIFICATION_PAGING),
    );

    clearedPaging: SortablePagedRequestNotificationSortBy = JSON.parse(
        JSON.stringify(DEFAULT_CLEARED_NOTIFICATION_PAGING),
    );

    notificationPageSize = new BehaviorSubject<number>(
        DEFAULT_WORKLIST_PAGESIZE.pageSize,
    );

    ALL_OPEN_NOTIFICATIONS: BehaviorSubject<NotificationResponse[]> =
        new BehaviorSubject<NotificationResponse[]>(null);

    ALL_CLEARED_NOTIFICATIONS: BehaviorSubject<NotificationResponse[]> =
        new BehaviorSubject<NotificationResponse[]>(null);

    setOpenPageNumber(pageNumber: number) {
        this.openPaging.pageNumber = pageNumber;
    }

    setClearedPageNumber(pageNumber: number) {
        this.clearedPaging.pageNumber = pageNumber;
    }

    constructor(
        private service: NotificationControllerService,
        private enumService: EnumService,
    ) {}

    getNotificationOpenFilterOption(): Option {
        return this.notificationOpenFilter.value;
    }

    setNotificationOpenFilterOption(option: Option) {
        this.notificationOpenFilter.next(option);
    }

    getNotificationClearedFilterOption(): Option {
        return this.notificationClearedFilter.value;
    }

    setNotificationClearedFilterOption(option: Option) {
        this.notificationClearedFilter.next(option);
    }

    refreshOpenNotificationData() {
        this.setOpenPageNumber(1);
        this.getPagedOpenNotifications();
    }

    refreshClearedNotificationData() {
        this.setClearedPageNumber(1);
        this.getPagedClearedNotifications();
    }

    getPagedOpenNotifications() {
        let request: PagedNotificationSearchRequest = {
            statusList: [StatusListEnum.Active],
            createdOnRange: this.getCreatedDateRange(),
            paged: this.getOpenPaging(),
        };
        this.service
            .findAllNotificationsByPagedGeneralSearch(request)
            .pipe(take(1))
            .subscribe((results) => {
                this.PAGED_OPEN_NOTIFICATIONS.next(results);
            });
    }

    getPagedClearedNotifications() {
        let request: PagedNotificationSearchRequest = {
            statusList: [StatusListEnum.Inactive],
            clearedDateRange: this.getClearedDateRange(),
            paged: this.getClearedPaging(),
        };
        this.service
            .findAllNotificationsByPagedGeneralSearch(request)
            .pipe(take(1))
            .subscribe((results) => {
                this.PAGED_CLEARED_NOTIFICATIONS.next(results);
            });
    }

    findAllOpenNotifications() {
        return this.service
            .findActiveForAccount()
            .pipe(take(1))
            .subscribe((results) => {
                this.ALL_OPEN_NOTIFICATIONS.next(results);
            });
    }

    findAllClearedNotifications() {
        let request: PagedNotificationSearchRequest = {
            statusList: [StatusListEnum.Inactive],
            paged: {
                unPaged: true,
            },
        };

        return this.service
            .findAllNotificationsByPagedGeneralSearch(request)
            .pipe(take(1))
            .subscribe((results) => {
                this.ALL_CLEARED_NOTIFICATIONS.next(results.items);
            });
    }

    getOpenPaging(): SortablePagedRequestNotificationSortBy {
        return {
            pageNumber: this.openPaging.pageNumber,
            sortFieldName: this.openPaging.sortFieldName as SortFieldNameEnum,
            sortDirection: this.openPaging.sortDirection,
            unPaged: this.openPaging.unPaged,
            /* Use global page size */
            pageSize: this.getNotificationPageSize(),
        };
    }

    getClearedPaging(): SortablePagedRequestNotificationSortBy {
        return {
            pageNumber: this.clearedPaging.pageNumber,
            sortFieldName: this.clearedPaging
                .sortFieldName as SortFieldNameEnum,
            sortDirection: this.clearedPaging.sortDirection,
            unPaged: this.clearedPaging.unPaged,
            /* Use global page size */
            pageSize: this.getNotificationPageSize(),
        };
    }

    changePage(event: PageEvent, status: NotificationResponse.StatusEnum) {
        this.setPageSize(event.pageSize);
        if (status === StatusListEnum.Active) {
            this.openPaging.pageNumber = event.pageIndex + 1;
            this.getPagedOpenNotifications();
        } else {
            this.clearedPaging.pageNumber = event.pageIndex + 1;
            this.getPagedClearedNotifications();
        }
    }

    changeSort(
        event: { active: string; direction: SortDirectionEnum },
        status: NotificationResponse.StatusEnum,
    ) {
        if (status === StatusListEnum.Active) {
            this.openPaging.sortFieldName =
                this.enumService.transformToUpperCase(
                    event.active,
                ) as SortablePagedRequestNotificationSortBy.SortFieldNameEnum;
            this.openPaging.sortDirection =
                this.enumService.transformToUpperCase(
                    event.direction,
                ) as SortDirectionEnum;
            this.getPagedOpenNotifications();
        } else {
            this.clearedPaging.sortFieldName =
                this.enumService.transformToUpperCase(
                    event.active,
                ) as SortablePagedRequestNotificationSortBy.SortFieldNameEnum;
            this.clearedPaging.sortDirection =
                this.enumService.transformToUpperCase(
                    event.direction,
                ) as SortDirectionEnum;
            this.getPagedClearedNotifications();
        }
    }

    getNotificationPageSize() {
        return this.notificationPageSize.value;
    }

    setPageSize(pageSize: number) {
        this.notificationPageSize.next(pageSize);
    }

    getCreatedDateRange() {
        const selectedFilter = this.notificationOpenFilter.value;
        let range: InstantRange;

        switch (selectedFilter.value) {
            case 0:
                range = {
                    minInstant: moment()
                        .subtract(10, 'days')
                        .startOf('day')
                        .format('YYYY-MM-DDTHH:mm:ss[Z]'),
                    maxInstant: moment()
                        .utc()
                        .endOf('day')
                        .format('YYYY-MM-DDTHH:mm:ss[Z]'),
                };
                break;
            case 1:
                range = {
                    minInstant: moment()
                        .subtract(30, 'days')
                        .startOf('day')
                        .format('YYYY-MM-DDTHH:mm:ss[Z]'),
                    maxInstant: moment()
                        .subtract(11, 'days')
                        .endOf('day')
                        .format('YYYY-MM-DDTHH:mm:ss[Z]'),
                };
                break;
            case 2:
                range = {
                    minInstant: moment()
                        .subtract(90, 'days')
                        .startOf('day')
                        .format('YYYY-MM-DDTHH:mm:ss[Z]'),
                    maxInstant: moment()
                        .subtract(31, 'days')
                        .endOf('day')
                        .format('YYYY-MM-DDTHH:mm:ss[Z]'),
                };
                break;
            case 3:
                range = null;
                break;
        }
        return range;
    }

    getClearedDateRange() {
        const selectedFilter = this.notificationClearedFilter.value;
        let range: LocalDateTimeRange;

        switch (selectedFilter.value) {
            case 0:
                range = {
                    minDateTime: moment()
                        .subtract(10, 'days')
                        .startOf('day')
                        .format('YYYY-MM-DDTHH:mm:ss[Z]'),
                    maxDateTime: moment()
                        .utc()
                        .endOf('day')
                        .format('YYYY-MM-DDTHH:mm:ss[Z]'),
                };
                break;
            case 1:
                range = {
                    minDateTime: moment()
                        .subtract(30, 'days')
                        .startOf('day')
                        .format('YYYY-MM-DDTHH:mm:ss[Z]'),
                    maxDateTime: moment()
                        .subtract(11, 'days')
                        .endOf('day')
                        .format('YYYY-MM-DDTHH:mm:ss[Z]'),
                };
                break;
            case 2:
                range = {
                    minDateTime: moment()
                        .subtract(90, 'days')
                        .startOf('day')
                        .format('YYYY-MM-DDTHH:mm:ss[Z]'),
                    maxDateTime: moment()
                        .subtract(31, 'days')
                        .endOf('day')
                        .format('YYYY-MM-DDTHH:mm:ss[Z]'),
                };
                break;
            case 3:
                range = null;
                break;
        }
        return range;
    }
}
